import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from '@mui/material';
import markdown from '../../utils/helpers/mardown-parser';
import './CookieBanner.scss';
import {event, saveConsentSettngs} from '../../utils/helpers/analytics';
import {useCookies} from 'react-cookie';
import {useContentProxyData} from "../../hooks/useContentProxyData";
import {ContentProxyAppSettings, EditorialText} from "../../models/contentProxyModel";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";

const cookieIdEnum = {
    'NECESSARY COOKIES': 'C0001',
    'FUNCTIONAL COOKIES': 'C0003',
    'ANALYTIC COOKIES': 'C0002',
    'TARGETING COOKIES': 'C0004',
};

const CookieBanner = () => {
    const {data} = useContentProxyData<ContentProxyAppSettings>('appsettings', 600000);
    const cookieName = 'sgp-cp-acceptance';
    const [cookies, setCookie] = useCookies();
    const [isVisible, setIsVisible] = useState(false);
    const [redirectToCookiesPage, setRedirectToCookiesPage] = useState(false);
    const [cookieValues, setCookieValues] = useState({
        'NECESSARY COOKIES': true,
        'FUNCTIONAL COOKIES': true,
        'ANALYTIC COOKIES': true,
        'TARGETING COOKIES': true,
    });
    const [cookieDescription, setCookieDescription] = useState<EditorialText[]>()
    const cookiePageUrl = 'https://sailgp.com/general/cookie/'
    const continueLabel = "Accept all cookies";
    const learnMoreLabel = "Learn More"
    const cookieMessage = "# *THIS WEBSITE USES COOKIES*\nWe use them to give you the best experience. If you continue using our website, we'll assume that you are happy to receive all cookies on this website."
    const rejectLabel = "Reject all cookies"
    const saveAndCloseLabel = "Save and close"

    const action = useCallback((optionSelected: string, action: string, id: string | null, category?: string) => {
        event({
            eventName: 'Cookie Banner',
            category: category || optionSelected,
            action: action,
            label: optionSelected,
            Label_ID: id,
        });
    }, []);

    const appSettings = useMemo((): ContentProxyAppSettings | undefined => {
        if (!data) {
            return undefined;
        }
        return data;
    }, [data]);

    useEffect(() => {
        if (appSettings?.app_insightsCookieBanner) setCookieDescription(appSettings?.app_insightsCookieBanner)
    }, [appSettings])

    useEffect(() => {
        setIsVisible(cookies[cookieName] !== 'true');
    }, []);

    useEffect(() => {
        if (redirectToCookiesPage && cookiePageUrl) {
            window.open(
                'https://sailgp.com/general/cookie/',
                '_blank'
            );
        }
    }, [redirectToCookiesPage]);

    const setPolicyAcceptanceCookie = (name, value) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
        setCookie(name, value, {path: '/', expires: expires, domain: '.sailgp.com'});
    };

    const acceptCookies = () => {

        const consentSettings = {
            ad_storage: 'granted',
            analytics_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            functionality_storage: 'granted',
            personalization_storage: 'granted',
        };

        // Save consent settings to local storage
        localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));

        // Save consent settings to cookie
        const expires = new Date();
        expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
        document.cookie = `cookieConsent=${JSON.stringify(consentSettings)}; expires=${expires.toUTCString()}; path=/`;

        // Call official Google Consent Mode v2 API
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('consent', 'update', {
                ad_storage: 'granted',
                analytics_storage: 'granted',
                ad_user_data: 'granted',
                ad_personalization: 'granted',
                functionality_storage: 'granted',
                personalization_storage: 'granted',
            });
        }

        setPolicyAcceptanceCookie(cookieName, 'true');
        action(continueLabel, 'Option Selected', null, 'Cookie Banner');
        cookieDescription?.forEach(({heading}) => action(heading, 'accept', cookieIdEnum[heading]));
        saveConsentSettngs(consentSettings);
        setIsVisible(false);
    };

    const rejectCookies = () => {
        const consentSettings = {
            ad_storage: 'denied',
            analytics_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            functionality_storage: 'granted', // Functional cookies remain allowed
            personalization_storage: 'denied',
        };

        // Save consent settings to local storage
        localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));

        // Save consent settings to cookie
        const expires = new Date();
        expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
        document.cookie = `cookieConsent=${JSON.stringify(consentSettings)}; expires=${expires.toUTCString()}; path=/`;

        // Call official Google Consent Mode v2 API
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('consent', 'update', {
                ad_storage: 'denied',
                analytics_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
                functionality_storage: 'granted',
                personalization_storage: 'denied',
            });
        }

        setPolicyAcceptanceCookie(cookieName, 'true');
        action(rejectLabel, 'Option Selected', null, 'Cookie Banner');
        cookieDescription?.forEach(({heading}) => {
            if (heading === 'NECESSARY COOKIES') action(heading, 'accept', cookieIdEnum[heading]);
            else action(heading, 'reject', cookieIdEnum[heading]);
        });
        saveConsentSettngs(consentSettings);
        setIsVisible(false);
    };

    const eachCookies = (e, cookieHeading) => {
        setCookieValues((prevState) => ({...prevState, [cookieHeading]: e.target.checked}));
    };

    const learnMore = () => {
        action(learnMoreLabel, 'Option Selected', null, 'Cookie Banner');
        setRedirectToCookiesPage(true);
    };

    const saveAndClose = () => {
        const consentSettings = {
            ad_storage: cookieValues['TARGETING COOKIES'] ? 'granted' : 'denied',
            analytics_storage: cookieValues['ANALYTIC COOKIES'] ? 'granted' : 'denied',
            ad_user_data: cookieValues['TARGETING COOKIES'] ? 'granted' : 'denied',
            ad_personalization: cookieValues['TARGETING COOKIES'] ? 'granted' : 'denied',
            functionality_storage: cookieValues['FUNCTIONAL COOKIES'] ? 'granted' : 'denied',
            personalization_storage: cookieValues['TARGETING COOKIES'] ? 'granted' : 'denied',
        };

        // Save consent settings to local storage
        localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));

        // Save consent settings to cookie
        const expires = new Date();
        expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
        document.cookie = `cookieConsent=${JSON.stringify(consentSettings)}; expires=${expires.toUTCString()}; path=/`;

        // Call official Google Consent Mode v2 API
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('consent', 'update', {
                ad_storage: consentSettings.ad_storage,
                analytics_storage: consentSettings.analytics_storage,
                ad_user_data: consentSettings.ad_user_data,
                ad_personalization: consentSettings.ad_personalization,
                functionality_storage: consentSettings.functionality_storage,
                personalization_storage: consentSettings.personalization_storage,
            });
        }

        setPolicyAcceptanceCookie(cookieName, 'true');
        for (const key in cookieValues) {
            if (cookieValues[key]) action(key, 'accept', cookieIdEnum[key]);
            else action(key, 'reject', cookieIdEnum[key]);
        }
        saveConsentSettngs(consentSettings);
        setIsVisible(false);
    };

    if (isVisible && data) {
        return (
            <section className='c-cookie-banner'>
                <div className='c-cookie-banner_modal'>
                    <div className='c-cookie-banner_modal_inner'>
                        <div className={'c-cookie-banner_modal_inner-text'}
                             dangerouslySetInnerHTML={{__html: markdown(cookieMessage)}}/>
                    </div>

                    <Button className='c-button c-button--primary top-button' onClick={acceptCookies}>
                        {continueLabel}
                    </Button>
                    <Button className='c-button c-button--secondary top-button' onClick={rejectCookies}>
                        {rejectLabel}
                    </Button>
                    <div className='c-cookie-banner_modal_inner'>
                        <div className='modal-scroll'>
                            {cookieDescription &&
                                cookieDescription.map((item) => (
                                    <div key={item.contentfulId}>
                                        <div className='cookies-toggle-button'>
                                            <h3>{item.heading}</h3>
                                            {item.heading === 'NECESSARY COOKIES' ? null : (
                                                <ToggleSwitch
                                                    id={`cookie-toggle-${item.contentfulId}`}
                                                    checked={cookieValues[item.heading]}
                                                    onChange={(e) => {
                                                        eachCookies(e, item.heading);
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className='cookie-text'>
                                            <p>{item.content}</p>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                    {cookieDescription && (
                        <Button className='c-button bottom-button c-button--primary' onClick={saveAndClose}>
                            {saveAndCloseLabel}
                        </Button>
                    )}
                    <Button
                        className={`c-button c-button--secondary ${cookieDescription ? 'bottom-button' : 'bottom-button learn-more'}`}
                        onClick={learnMore}
                    >
                        {learnMoreLabel}
                    </Button>
                </div>
                <div className='c-cookie-banner_background'></div>
            </section>
        );
    } else {
        return null;
    }
};

export default CookieBanner;
